import utilsService from "api/utils/utils.service";
import { IUtilsGenerateUploadUrlResponse } from "api/utils/utils.type";
import { useUploadImagePreparation } from "hooks/useUploadImagePreparation";
import { UPLOAD_HARD_MAX_MB } from "utils/uploadImageOptimizer";
import { IconWarning } from "icons";
import { useDispatch } from "react-redux";
import { setSnackbar } from "store/commonSlice";
import { UPLOAD_CONCURRENCY, mapWithConcurrency } from "utils/concurrency";
import { MAX_UPLOAD_LIMIT_BYTES, MAX_UPLOAD_LIMIT_MB } from "utils/constants";
import { EventUploadLimitError } from "utils/events";
import { bakeExifOrientation } from "utils/exifOrientation";
import { reportError } from "utils/sentry";
import { v4 as uuidv4 } from "uuid";
import i18n from "i18n";
import { httpStatusOf } from "utils/httpStatusOf";

// A PUT that fails gets ONE more attempt with a FRESHLY SIGNED url. Never retry the same
// url: if the failure was signature expiry, replaying it fails identically forever.
const UPLOAD_PUT_ATTEMPTS = 2;

type UploadFailureStage = "exif" | "sign" | "put";

export interface IGCSUploadResult {
  Key: string;
  Location: string;
}

export interface IGCSUploadOutcome {
  file: any;
  ok: boolean;
  result?: IGCSUploadResult;
  stage?: UploadFailureStage;
  status?: number;
}

export interface IGCSBatchOutcome {
  // Per-file outcomes for every ATTEMPTED file, in input order. Consumers that pair a file with
  // its url (a file name, a training item, a product row) MUST use this — pairing
  // `succeeded[i]` with `files[i]` silently mislabels everything after the first failure.
  outcomes: IGCSUploadOutcome[];
  // Successful uploads, in the order the files were given. This is what the legacy
  // `{ Key, Location }[]` return value is built from.
  succeeded: IGCSUploadResult[];
  failed: IGCSUploadOutcome[];
  // Files dropped by the size limit before any request was made.
  rejectedForSize: any[];
  requestedCount: number;
  durationMs: number;
}

interface IGCSUpload {
  files: any[];
  folderName?: string;
  isLimited?: boolean;
  maxSize?: number;
  // Per-file escape hatch from the size limit — ZIP/spreadsheet payloads whose real ceiling
  // is the backend pipeline's, not the per-image one. `UploadBox` derived this itself and then
  // had to collapse it into `isLimited: false` for the whole batch; raw callers had no way to
  // express it at all, which is how a Photoshoot Studio ZIP ended up measured against the
  // 15MB image limit.
  isSizeExempt?: (file: any) => boolean;
}

interface IGCSSingleUpload {
  file: any;
  folderName?: string;
}

// Error carrying the stage it failed at, so a batch report can say WHERE the batch broke
// (our API vs GCS) instead of just "something went wrong".
class UploadStageError extends Error {
  stage: UploadFailureStage;
  status?: number;

  constructor(message: string, stage: UploadFailureStage, status?: number) {
    super(message);
    this.name = "UploadStageError";
    this.stage = stage;
    this.status = status;
  }
}

const useGoogleStorage = () => {
  const dispatch = useDispatch();
  const { prepareFiles } = useUploadImagePreparation();

  async function getSignedUrl(fileName: string, contentType: string) {
    try {
      const response: IUtilsGenerateUploadUrlResponse = await utilsService.generateUploadUrl({ file_name: fileName, content_type: contentType });

      if (typeof response.upload_url !== "string" || typeof response.file_url !== "string" || typeof response.file_name !== "string") {
        throw new Error("generate_upload_url response is missing upload_url, file_url, or file_name");
      }

      return {
        signedUrl: response.upload_url,
        fileName: response.file_name,
        fileUrl: response.file_url,
        // Carried through UNREAD. Naming the header here would put a second copy of the
        // contract in the client, and the value is env-tunable on the server side
        // (MAX_SIGNED_UPLOAD_BYTES) — a copy that disagrees with the signature is a 403 on
        // every upload, not a looser bound.
        uploadHeaders: response.upload_headers,
      };
    } catch (error) {
      console.error("Error getting signed URL", { status: httpStatusOf(error) });
      throw error;
    }
  }

  async function uploadToSignedUrl(url: string, file: Blob, contentType: string, uploadHeaders?: Record<string, string>) {
    try {
      // The Blob goes to `fetch` as-is. `await file.arrayBuffer()` used to copy every byte
      // into a second buffer first, doubling peak memory for the whole batch.
      const response = await fetch(url, {
        method: "PUT",
        headers: {
          // Content-Type is signed too, and always was — it must match the `content_type`
          // the url was requested with. That is the other way to get a
          // SignatureDoesNotMatch, so check it before blaming the header below.
          "Content-Type": contentType,
          // Whatever the server signed, echoed verbatim (REF-6408). Spread, never rebuilt:
          // these ARE the signature, so a value we compose ourselves is a guess. Absent on
          // an older backend, and then this spreads nothing and behaves exactly as before —
          // which is what lets this ship without waiting for the API.
          ...(uploadHeaders ?? {}),
        },
        body: file,
      });

      if (!response.ok) {
        // REF-6708: the body is still read to the end (the connection is released exactly as before), but its
        // text goes nowhere — not the console, and not the error's message, which `uploadBase64` hands to
        // Sentry via `reportError(error)`. The storage backend can echo the signed request back in it.
        await response.text();
        console.error("Upload error response", { status: response.status });
        throw new UploadStageError(`Upload failed: HTTP ${response.status}`, "put", response.status);
      }

      return response;
    } catch (error) {
      console.error("Error uploading to signed URL", { status: httpStatusOf(error) });
      throw error;
    }
  }

  // Signs and PUTs one file. The url is requested immediately before its own PUT — never
  // up-front for the whole batch, whose tail would then be racing a 15-minute expiry.
  async function uploadOne(file: any): Promise<IGCSUploadOutcome> {
    const fileExtension = file?.name?.split(".").pop();
    const fileName = `${uuidv4()}.${fileExtension}`;

    let payload: Blob = file;

    try {
      // Bake EXIF orientation into the pixels before the bytes reach GCS — this upload path
      // bypasses the backend entirely, so a rotated JPEG would be processed sideways (REF-5982)
      payload = await bakeExifOrientation(file);
    } catch (error) {
      // bakeExifOrientation is documented fail-open; treat a throw the same way rather than
      // losing the file — a rotated upload beats a dropped one.
      payload = file;
    }

    // Sticky across attempts: if attempt 1's PUT failed and attempt 2's RE-SIGN then failed,
    // the batch must still know a transport failure happened. Reporting it as a bare "sign"
    // failure hid the real GCS problem.
    let putFailureStatus: number | undefined;
    let sawPutFailure = false;

    for (let attempt = 1; attempt <= UPLOAD_PUT_ATTEMPTS; attempt += 1) {
      let signedUrl: string;
      let fileUrl: string;

      let uploadHeaders: Record<string, string> | undefined;

      try {
        const signed = await getSignedUrl(fileName, file.type);
        signedUrl = signed.signedUrl;
        fileUrl = signed.fileUrl;
        uploadHeaders = signed.uploadHeaders;
      } catch (error) {
        return { file, ok: false, stage: sawPutFailure ? "put" : "sign", status: putFailureStatus };
      }

      try {
        await uploadToSignedUrl(signedUrl, payload, file.type, uploadHeaders);

        return { file, ok: true, result: { Key: fileName, Location: fileUrl } };
      } catch (error) {
        const status = error instanceof UploadStageError ? error.status : undefined;
        const isLastAttempt = attempt === UPLOAD_PUT_ATTEMPTS;

        sawPutFailure = true;
        putFailureStatus = status ?? putFailureStatus;

        if (isLastAttempt) return { file, ok: false, stage: "put", status };
      }
    }

    return { file, ok: false, stage: "put", status: putFailureStatus };
  }

  // Reports the transport failures in a settled batch. Expected rejections (size, type,
  // dimensions, policy) are NOT errors and never come here — they are user-facing validation
  // with their own analytics. Only unexpected transport failure is a Sentry event, and it is
  // ONE aggregated event per batch, never one per file.
  function reportBatchFailures(outcome: IGCSBatchOutcome, folderName: string) {
    if (outcome.failed.length === 0) return;

    // ONE aggregate event per settled batch, uniquely fingerprinted (`gcs-batch-upload`).
    //
    // An earlier revision suppressed batches whose failures were all at the SIGNING stage, on
    // the grounds that `HttpClient` already reports those. That was too broad: `HttpClient`
    // deliberately does NOT report a handled 503, a network error with no status, or a resolved
    // response with a malformed body — all of which land here as sign-stage outcomes. Suppressing
    // them produced batches that failed entirely and reported NOTHING, which is the exact
    // blindness this whole change exists to remove. The per-request events HttpClient does emit
    // are a different granularity with a different fingerprint, so they do not collide.

    const attempted = outcome.requestedCount - outcome.rejectedForSize.length;
    const allFailed = outcome.failed.length === attempted;
    const stages = Array.from(new Set(outcome.failed.map((item) => item.stage).filter(Boolean)));
    const statuses = Array.from(new Set(outcome.failed.map((item) => item.status).filter((status): status is number => typeof status === "number")));

    reportError(new Error(`GCS batch upload failed for ${outcome.failed.length}/${attempted} files`), {
      source: "manual",
      featureArea: "media",
      level: allFailed ? "error" : "warning",
      route: window.location.pathname,
      method: "PUT",
      fingerprint: ["gcs-batch-upload", allFailed ? "total" : "partial", stages.join(",") || "unknown"],
      extras: {
        // Counts and stages only — never file names, bytes, or the signed url (it carries a
        // signature in its query string).
        folder_name: folderName,
        requested_count: outcome.requestedCount,
        attempted_count: attempted,
        succeeded_count: outcome.succeeded.length,
        failed_count: outcome.failed.length,
        rejected_for_size_count: outcome.rejectedForSize.length,
        failed_stages: stages,
        failed_http_statuses: statuses,
        duration_ms: outcome.durationMs,
        max_concurrency: UPLOAD_CONCURRENCY,
      },
    });
  }

  // Multi-file upload with per-file outcomes. Prefer this over `uploadFiles` when the caller
  // wants to tell the user what actually happened; `uploadFiles` keeps the legacy array shape.
  async function uploadFilesDetailed({ files, folderName = "style_temp/", isLimited = true, maxSize = MAX_UPLOAD_LIMIT_MB, isSizeExempt }: IGCSUpload): Promise<IGCSBatchOutcome> {
    const startedAt = Date.now();
    const sizeLimit = maxSize * 1024 * 1024;
    const exempt = (file: any) => (isSizeExempt ? isSizeExempt(file) : false);
    // An image over the cap is optimized before the size filter instead of being dropped by it — the
    // last line of defence for callers that reach storage without going through UploadBox or
    // `useFileValidation` (both of which already did this, so their files arrive under the cap and
    // pass straight through). `prepared[i]` is what gets PUT; `files[i]` stays the caller's identity.
    // Not for a caller whose own cap is ABOVE the shared ceiling (folder inputs, the styling guide) —
    // those already accept more, and keep exactly the behaviour they had.
    const optimizes = isLimited && sizeLimit <= MAX_UPLOAD_LIMIT_BYTES;
    const preparation = optimizes ? await prepareFiles(files, { maxBytes: sizeLimit, isSizeExempt: exempt }) : null;
    const prepared: any[] = preparation ? preparation.files : files;
    // Size filtering applies ONLY when the caller asked for it. It used to run unconditionally,
    // so `isLimited: false` still silently dropped every file over 15MB — and without even the
    // snackbar, because only the message was gated on the flag.
    const fits = (index: number) => !isLimited || prepared[index].size <= sizeLimit || exempt(files[index]);
    const indexes = files.map((_, index) => index);
    const overSizedFiles = indexes.filter((index) => !fits(index)).map((index) => files[index]);
    const rightSizedIndexes = indexes.filter(fits);

    if (overSizedFiles.length > 0) {
      // Over the hard max nothing could be attempted, so that is the limit worth naming; a file the
      // optimizer could not get under the cap is told the cap.
      const allTooLarge = !!preparation && indexes.filter((index) => !fits(index)).every((index) => preparation.statuses[index] === "too_large");
      dispatch(
        setSnackbar({
          message: i18n.t("validation:file.size", { limit: allTooLarge ? UPLOAD_HARD_MAX_MB : maxSize }),
          icon: IconWarning,
        })
      );

      const pathname = window.location.pathname;
      const cleanPathname = pathname.startsWith("/") ? pathname.substring(1) : pathname;
      EventUploadLimitError(cleanPathname);
    }

    // Bounded, and every file settles on its own. One failure used to reject the whole
    // `Promise.all`, whose catch returned `[]` — discarding files that had already landed in
    // the bucket and leaving them orphaned there with no record.
    //
    // `outcome.file` is the caller's ORIGINAL file, not the optimized copy that was sent: consumers
    // pair results back to their own selection by object identity (LookBuilder's `urlByFile`).
    const outcomes = await mapWithConcurrency(rightSizedIndexes, UPLOAD_CONCURRENCY, async (index) => ({ ...(await uploadOne(prepared[index])), file: files[index] }));

    const outcome: IGCSBatchOutcome = {
      outcomes,
      succeeded: outcomes.filter((item) => item.ok && item.result).map((item) => item.result as IGCSUploadResult),
      failed: outcomes.filter((item) => !item.ok),
      rejectedForSize: overSizedFiles,
      requestedCount: files.length,
      durationMs: Date.now() - startedAt,
    };

    reportBatchFailures(outcome, folderName);

    return outcome;
  }

  async function uploadFiles(params: IGCSUpload) {
    const outcome = await uploadFilesDetailed(params);

    // Partial success is returned, not thrown away. Callers already treat the array as "what
    // got uploaded" and check its length, so they keep working — they just stop losing the
    // files that succeeded.
    if (outcome.failed.length > 0) {
      dispatch(setSnackbar({ message: i18n.t("validation:file.upload_failed"), icon: IconWarning }));
    }

    return outcome.succeeded;
  }

  async function uploadFile({ files, folderName = "style_temp/", isLimited = true, maxSize = MAX_UPLOAD_LIMIT_MB, isSizeExempt }: IGCSUpload) {
    const sizeLimit = maxSize * 1024 * 1024;
    const exempt = isSizeExempt ? isSizeExempt(files[0]) : false;
    // Same as the batch path: optimize an image over the cap instead of rejecting it.
    const preparation = isLimited && !exempt && sizeLimit <= MAX_UPLOAD_LIMIT_BYTES ? await prepareFiles(files, { maxBytes: sizeLimit }) : null;
    const prepared: any[] = preparation ? preparation.files : files;
    const overSizedFiles = exempt ? [] : prepared.filter((file) => file.size > sizeLimit);

    if (isLimited && overSizedFiles.length > 0) {
      dispatch(
        setSnackbar({
          message: i18n.t("validation:file.size", { limit: preparation?.statuses[0] === "too_large" ? UPLOAD_HARD_MAX_MB : maxSize }),
          icon: IconWarning,
        })
      );

      const pathname = window.location.pathname;
      const cleanPathname = pathname.startsWith("/") ? pathname.substring(1) : pathname;
      EventUploadLimitError(cleanPathname);

      return [];
    }

    const startedAt = Date.now();
    const outcome = { ...(await uploadOne(prepared[0])), file: files[0] };

    if (!outcome.ok || !outcome.result) {
      reportBatchFailures(
        {
          outcomes: [outcome],
          succeeded: [],
          failed: [outcome],
          rejectedForSize: [],
          requestedCount: 1,
          durationMs: Date.now() - startedAt,
        },
        folderName
      );
      dispatch(setSnackbar({ message: i18n.t("validation:file.upload_failed"), icon: IconWarning }));

      return [];
    }

    return [outcome.result];
  }

  async function uploadBase64({ file, folderName = "style_temp/" }: IGCSSingleUpload) {
    try {
      const type = file.split(";")[0].split("/")[1];

      const base64Response = await fetch(file);
      const blob = await base64Response.blob();

      const sizeLimit = MAX_UPLOAD_LIMIT_MB * 1024 * 1024;
      if (blob.size > sizeLimit) {
        dispatch(setSnackbar({ message: i18n.t("validation:file.size", { limit: MAX_UPLOAD_LIMIT_MB }), icon: IconWarning }));
        const pathname = window.location.pathname;
        EventUploadLimitError(pathname.startsWith("/") ? pathname.substring(1) : pathname);

        return [];
      }

      const fileName = `canvasItem_${uuidv4()}.${type}`;
      // `uploadHeaders` is NOT optional here, whatever its type says. Anything the server
      // signed is part of the V4 signature, so a PUT that drops it is refused outright:
      // GCS answers `MalformedSecurityHeader — Header was included in signedheaders, but
      // not in the request`. This call site was missed when the header was threaded through
      // `uploadOne`, and stage failed on every canvas upload until it was noticed by hand —
      // the tests only exercised the batch path.
      const { signedUrl, fileUrl, uploadHeaders } = await getSignedUrl(fileName, `image/${type}`);
      await uploadToSignedUrl(signedUrl, blob, `image/${type}`, uploadHeaders);

      return [
        {
          Key: fileName,
          Location: fileUrl,
        },
      ];
    } catch (error) {
      // This catch is why the base64-in-node-data bug had no evidence trail: every caller sees only
      // an empty array, so a failed upload was indistinguishable from "nothing to upload". Report it
      // — the trigger behind the ~0.8% of magic selections that shipped base64 is still unknown.
      reportError(error, {
        source: "manual",
        featureArea: "media",
        level: "error",
        route: window.location.pathname,
        extras: {
          folderName,
          dataUrlLength: typeof file === "string" ? file.length : undefined,
          mimeType: typeof file === "string" ? file.slice(5, file.indexOf(";")) : undefined,
        },
      });
      dispatch(setSnackbar({ message: i18n.t("validation:file.upload_failed"), icon: IconWarning }));

      return [];
    }
  }

  async function handleUpload({ files, folderName = "style_temp/", isLimited = true, maxSize = MAX_UPLOAD_LIMIT_MB, isSizeExempt }: IGCSUpload) {
    if (files.length === 1) return uploadFile({ files, folderName, isLimited, maxSize, isSizeExempt });
    else return uploadFiles({ files, folderName, isLimited, maxSize, isSizeExempt });
  }

  return {
    uploadFile,
    uploadFiles,
    uploadFilesDetailed,
    handleUpload,
    uploadBase64,
  };
};

export default useGoogleStorage;
